/**
 * SPEC-20260507-2200: tela admin de variáveis GLOBAIS de saga.
 *
 * Variáveis aqui se aplicam a QUALQUER saga + QUALQUER tenant — defaults
 * genéricos (ex: DEFAULT_REGION, DEFAULT_KUBECONFIG_PATH, COMPANY_DOMAIN).
 * Tenant + sagaType variables sobrescrevem estas.
 */

import { useEffect, useState } from "react";
import { Loader2, Plus, Lock, Eye, EyeOff, Trash2, Settings2, Globe } from "lucide-react";
import { toast } from "sonner";
import {
  listGlobalVariables,
  upsertGlobalVariable,
  deleteGlobalVariable,
  revealGlobalVariable,
  type SagaVariableView,
  type SagaVariableValueType,
} from "../services/saga-variables";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RequireRole } from "../components/RequireRole";
import { cn } from "../utils/cn";

export default function GlobalSagaVariablesPage() {
  return (
    <RequireRole anyOf={["platform-master", "platform-admin"]}>
      <Content />
    </RequireRole>
  );
}

function Content() {
  const [variables, setVariables] = useState<SagaVariableView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<SagaVariableView | null>(null);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<Map<string, string>>(new Map());

  async function refresh() {
    setLoading(true);
    try {
      const data = await listGlobalVariables();
      setVariables(data);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao listar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleReveal(name: string) {
    if (revealed.has(name)) {
      const next = new Map(revealed);
      next.delete(name);
      setRevealed(next);
      return;
    }
    try {
      const value = await revealGlobalVariable(name);
      setRevealed((prev) => new Map(prev).set(name, value));
      setTimeout(() => {
        setRevealed((prev) => {
          const n = new Map(prev);
          n.delete(name);
          return n;
        });
      }, 30_000);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao revelar");
    }
  }

  async function handleDelete(name: string) {
    if (!confirm(`Remover variável global '${name}'? Não pode ser desfeito.`)) return;
    try {
      await deleteGlobalVariable(name);
      toast.success(`Removida ${name}`);
      void refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    }
  }

  return (
    <>
      <PageHeader
        title="Variáveis Globais de Saga"
        subtitle="Valores aplicáveis a qualquer saga + qualquer tenant. Tenant+sagaType sobrescreve."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus size={14} /> Nova variável
          </Button>
        }
      />

      <div className="px-6 py-4 space-y-3">
        {err && (
          <div className="rounded border border-danger/30 bg-danger-soft/40 p-3 text-sm text-danger-soft-fg">
            {err}
          </div>
        )}

        {loading && !variables && (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> carregando…
          </div>
        )}

        {variables && variables.length === 0 && (
          <div className="text-sm text-muted-foreground italic text-center py-8 flex flex-col items-center gap-2">
            <Globe size={24} className="text-muted-foreground/50" />
            Nenhuma variável global configurada.
          </div>
        )}

        {variables && variables.length > 0 && (
          <div className="rounded border border-border bg-surface-1 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Nome</th>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">Valor</th>
                  <th className="text-left px-3 py-2">Descrição</th>
                  <th className="text-right px-3 py-2 w-28">Ações</th>
                </tr>
              </thead>
              <tbody>
                {variables.map((v) => {
                  const isRevealed = revealed.has(v.name);
                  return (
                    <tr key={v.id} className="border-t border-border-subtle">
                      <td className="px-3 py-2 font-mono-tight text-xs">
                        <div className="flex items-center gap-1.5">
                          {v.isSensitive && <Lock size={11} className="text-danger" />}
                          {v.name}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{v.valueType}</td>
                      <td className="px-3 py-2 font-mono-tight text-xs">
                        {v.isSensitive ? (isRevealed ? revealed.get(v.name) : "•••••") : v.value}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {v.description ?? <span className="italic">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {v.isSensitive && (
                            <Button
                              size="sm"
                              variant="outline"
                              type="button"
                              onClick={() => handleReveal(v.name)}
                              title={isRevealed ? "Esconder" : "Revelar (30s)"}
                            >
                              {isRevealed ? <EyeOff size={11} /> : <Eye size={11} />}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" type="button" onClick={() => setEditing(v)}>
                            <Settings2 size={11} />
                          </Button>
                          <Button size="sm" variant="outline" type="button" onClick={() => handleDelete(v.name)}>
                            <Trash2 size={11} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(creating || editing) && (
        <VariableModal
          variable={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </>
  );
}

interface VariableModalProps {
  variable: SagaVariableView | null;
  onClose(): void;
  onSaved(): void;
}

function VariableModal({ variable, onClose, onSaved }: VariableModalProps) {
  const [name, setName] = useState(variable?.name ?? "");
  const [valueType, setValueType] = useState<SagaVariableValueType>(variable?.valueType ?? "string");
  const [value, setValue] = useState(variable?.isSensitive ? "" : (variable?.value ?? ""));
  const [isSensitive, setIsSensitive] = useState(variable?.isSensitive ?? false);
  const [description, setDescription] = useState(variable?.description ?? "");
  const [choices, setChoices] = useState<string>(variable?.choices?.join(",") ?? "");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!name.trim() || !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name.trim())) {
      toast.error("Nome inválido — camelCase (mesmo nome usado em {{placeholder.X}}/{{secret.X}})");
      return;
    }
    if (!value.trim() && !variable?.isSensitive) {
      toast.error("Valor obrigatório");
      return;
    }
    setSaving(true);
    try {
      await upsertGlobalVariable({
        name: name.trim(),
        valueType,
        value: value.trim(),
        isSensitive,
        description: description.trim() || undefined,
        ...(valueType === "choice" ? { choices: choices.split(",").map((s) => s.trim()).filter(Boolean) } : {}),
      });
      toast.success(`Salvo ${name}`);
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-1 rounded-lg shadow-lg w-full max-w-md p-4 border border-border">
        <h2 className="font-medium text-base mb-3">
          {variable ? `Editar ${variable.name}` : "Nova variável global"}
        </h2>

        <div className="space-y-3">
          <div>
            <Label htmlFor="name">Nome (camelCase)</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!!variable}
              placeholder="defaultRegion"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Mesmo nome usado em <code className="font-mono-tight">{"{{placeholder.X}}"}</code> /{" "}
              <code className="font-mono-tight">{"{{secret.X}}"}</code> nos scripts.
            </p>
          </div>
          <div>
            <Label htmlFor="type">Tipo</Label>
            <select
              id="type"
              value={valueType}
              onChange={(e) => setValueType(e.target.value as SagaVariableValueType)}
              className="w-full px-2 py-1.5 text-sm border border-border rounded bg-bg"
            >
              <option value="string">string</option>
              <option value="number">number</option>
              <option value="boolean">boolean</option>
              <option value="date">date</option>
              <option value="datetime">datetime</option>
              <option value="choice">choice</option>
            </select>
          </div>
          {valueType === "choice" && (
            <div>
              <Label htmlFor="choices">Opções (separadas por vírgula)</Label>
              <Input
                id="choices"
                value={choices}
                onChange={(e) => setChoices(e.target.value)}
                placeholder="opt1, opt2, opt3"
              />
            </div>
          )}
          <div>
            <Label htmlFor="value">
              Valor {variable?.isSensitive && "(deixe vazio pra manter o atual)"}
            </Label>
            <Input
              id="value"
              type={isSensitive ? "password" : "text"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={variable?.isSensitive ? "(secret atual mantido)" : "valor..."}
            />
          </div>
          <div>
            <Label htmlFor="description">Descrição (opcional)</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Pra que serve esta variável"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={isSensitive}
              onChange={(e) => setIsSensitive(e.target.checked)}
            />
            <Lock size={12} className={cn(isSensitive ? "text-danger" : "text-muted-foreground")} />
            Sensitive (encrypt at-rest, mascarar em UI/logs)
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
